import React, { useEffect, useState } from "react";
import { api } from "@/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { CreditCard, CheckCircle2, XCircle, Loader2, Copy, ExternalLink } from "lucide-react";
import { toast } from "sonner";

export default function StripeConfigPanel({ onClose }) {
  const [cfg, setCfg] = useState({
    publishable_key: "", secret_key_set: false, webhook_secret_set: false, mode: "test", enabled: true,
  });
  const [pubKey, setPubKey] = useState("");
  const [secretKey, setSecretKey] = useState("");
  const [webhookSecret, setWebhookSecret] = useState("");
  const [mode, setMode] = useState("test");
  const [enabled, setEnabled] = useState(true);
  const [loading, setLoading] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const backendUrl = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
  const webhookUrl = `${backendUrl}/api/stripe/webhook`;

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/master/stripe/config");
      setCfg(r.data);
      setPubKey(r.data.publishable_key || "");
      setMode(r.data.mode || "test");
      setEnabled(r.data.enabled);
    } catch { toast.error("Erro ao carregar configuração"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setLoading(true);
    try {
      await api.put("/master/stripe/config", {
        publishable_key: pubKey,
        secret_key: secretKey || "",
        webhook_secret: webhookSecret || "",
        mode, enabled,
      });
      toast.success("Configuração salva");
      setSecretKey(""); setWebhookSecret("");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro ao salvar"); }
    finally { setLoading(false); }
  };

  const test = async () => {
    setTesting(true); setTestResult(null);
    try {
      const r = await api.post("/master/stripe/test-connection");
      setTestResult(r.data);
    } catch (e) { setTestResult({ ok: false, error: e?.response?.data?.detail || "Erro" }); }
    finally { setTesting(false); }
  };

  const copyWebhook = () => {
    navigator.clipboard.writeText(webhookUrl);
    toast.success("URL do webhook copiada");
  };

  return (
    <div className="space-y-4" data-testid="stripe-config-panel">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-display text-2xl flex items-center gap-2"><CreditCard size={22}/> Stripe</h2>
          <p className="text-xs text-neutral-500 mt-1">Configuração da integração de pagamentos.</p>
        </div>
        <div className="flex items-center gap-2">
          <Label htmlFor="stripe-enabled" className="text-xs">Ativa</Label>
          <Switch id="stripe-enabled" checked={enabled} onCheckedChange={setEnabled} data-testid="stripe-enabled-switch"/>
        </div>
      </div>

      <Card className="p-4 border-[#E5E5E5]">
        <div className="grid grid-cols-1 gap-3">
          <div>
            <Label className="text-xs">Ambiente</Label>
            <div className="flex gap-1.5 mt-1">
              {["test", "live"].map(v => (
                <button
                  key={v} type="button"
                  onClick={()=>setMode(v)}
                  className={`text-xs px-3 py-1.5 rounded border transition ${mode === v ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "bg-white border-[#E5E5E5]"}`}
                  data-testid={`stripe-mode-${v}`}
                >{v === "test" ? "Sandbox/Teste" : "Produção/Live"}</button>
              ))}
            </div>
          </div>

          <div>
            <Label className="text-xs">Publishable Key (pk_...)</Label>
            <Input value={pubKey} onChange={e=>setPubKey(e.target.value)} placeholder="pk_test_..." className="font-mono text-xs h-9" data-testid="stripe-pk"/>
          </div>
          <div>
            <Label className="text-xs">Secret Key (sk_...) {cfg.secret_key_set && <span className="ml-1 text-emerald-600">✓ salva</span>}</Label>
            <Input value={secretKey} onChange={e=>setSecretKey(e.target.value)} type="password" placeholder={cfg.secret_key_set ? "•••••• (deixe vazio pra manter)" : "sk_test_..."} className="font-mono text-xs h-9" data-testid="stripe-sk"/>
            <p className="text-[10px] text-neutral-500 mt-1">Nunca exposta no frontend. Fica apenas no servidor.</p>
          </div>
          <div>
            <Label className="text-xs">Webhook Signing Secret (whsec_...) {cfg.webhook_secret_set && <span className="ml-1 text-emerald-600">✓ salvo</span>}</Label>
            <Input value={webhookSecret} onChange={e=>setWebhookSecret(e.target.value)} type="password" placeholder={cfg.webhook_secret_set ? "•••••• (deixe vazio pra manter)" : "whsec_..."} className="font-mono text-xs h-9" data-testid="stripe-whsec"/>
          </div>

          <div className="pt-2 border-t">
            <Label className="text-xs">URL do webhook (configure no Stripe Dashboard)</Label>
            <div className="flex gap-2 mt-1">
              <Input value={webhookUrl} readOnly className="font-mono text-xs h-9 bg-neutral-50" data-testid="stripe-webhook-url"/>
              <Button size="sm" variant="outline" onClick={copyWebhook} data-testid="stripe-webhook-copy"><Copy size={12}/></Button>
            </div>
          </div>

          <div className="flex gap-2 pt-2">
            <Button onClick={save} disabled={loading} className="bg-[#0A0A0A] text-white" data-testid="stripe-save-btn">
              {loading ? <Loader2 size={14} className="animate-spin"/> : "Salvar"}
            </Button>
            <Button onClick={test} disabled={testing} variant="outline" data-testid="stripe-test-btn">
              {testing ? <Loader2 size={14} className="animate-spin mr-1"/> : <ExternalLink size={12} className="mr-1"/>}
              Testar conexão
            </Button>
            {testResult && (
              <div className={`text-xs px-3 py-1.5 rounded flex items-center gap-1 ${testResult.ok ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"}`} data-testid="stripe-test-result">
                {testResult.ok ? <CheckCircle2 size={12}/> : <XCircle size={12}/>}
                {testResult.ok
                  ? `Conectado a ${testResult.account_id || "conta"}${testResult.charges_enabled === false ? " (KYC pendente)" : ""}`
                  : `Falha: ${testResult.error || "erro desconhecido"}`}
              </div>
            )}
          </div>
        </div>
      </Card>

      {onClose && (
        <div className="flex justify-end">
          <Button variant="outline" onClick={onClose}>Fechar</Button>
        </div>
      )}
    </div>
  );
}
